import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { trackerCoachRequest } from '../tracker/trackerCoachClient';
import './practiceConfidence.css';

const choices = [['confident', 'Confident'], ['unsure', 'Unsure'], ['need-help', 'Need help']];
const blank = { loading: true, saving: false, value: '', error: '', retryValue: null };

export default function PracticeConfidence({ paperId, questionId, enabled }) {
    const { user, userData } = useAuth() || {};
    const allowed = userData?.role === 'student' && userData.assignedPapers?.includes(paperId);
    const key = JSON.stringify([user?.uid, paperId, questionId, Boolean(enabled && allowed)]);
    const [state, setState] = useState(blank);
    const [revision, setRevision] = useState(0);
    const requestNumber = useRef(0), currentKey = useRef(key), mounted = useRef(true), busy = useRef(false);
    currentKey.current = key;
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        const requests = requestNumber;
        const request = ++requests.current;
        busy.current = false;
        if (!user?.uid || !allowed || !enabled || !questionId) return;
        setState({ ...blank, key });
        trackerCoachRequest('question', { paperId, questionId }).then(result => {
            if (mounted.current && currentKey.current === key && request === requestNumber.current) {
                setState({ ...blank, key, loading: false, value: choices.some(([value]) => value === result?.question?.confidence) ? result.question.confidence : '' });
            }
        }).catch(() => {
            if (mounted.current && currentKey.current === key && request === requestNumber.current) setState({ ...blank, key, loading: false, error: 'Your saved confidence could not be loaded. Your answer is still saved.' });
        });
        return () => { requests.current++; };
    }, [key, user?.uid, allowed, enabled, paperId, questionId, revision]);
    const current = state.key === key ? state : blank;
    const save = async value => {
        if (busy.current || current.loading || !allowed || !enabled) return;
        const request = ++requestNumber.current;
        busy.current = true;
        setState(previous => ({ ...previous, key, saving: true, error: '', retryValue: value }));
        try {
            await trackerCoachRequest('confidence', { paperId, questionId, confidence: value });
            if (mounted.current && currentKey.current === key && requestNumber.current === request) setState({ ...blank, key, loading: false, value });
        } catch {
            if (mounted.current && currentKey.current === key && requestNumber.current === request) setState(previous => ({ ...previous, saving: false, retryValue: value, error: 'Your answer is saved, but this confidence choice could not be saved. Please retry.' }));
        } finally { if (currentKey.current === key && requestNumber.current === request) busy.current = false; }
    };
    if (!user?.uid || !allowed || !enabled || !questionId) return null;
    return <section className="iq-confidence" aria-label="Question confidence">
        <strong>How confident do you feel?</strong>
        <p>Unsure answers join your revision list. “Need help” also appears in your tutor’s tracker.</p>
        {current.loading ? <p role="status">Loading your confidence…</p> : <div className="iq-confidence-choices" role="group" aria-label="Choose confidence">{choices.map(([value, label]) => <button key={value} type="button" aria-pressed={current.value === value} disabled={current.saving || Boolean(current.error && current.retryValue === null)} onClick={() => save(value)}>{label}</button>)}</div>}
        {current.saving && <small role="status">Saving confidence…</small>}
        {!current.loading && !current.saving && !current.error && current.value && <small role="status">✓ Confidence saved to your account</small>}
        {current.error && <div className="iq-confidence-error" role="alert"><p>{current.error}</p><button type="button" disabled={current.saving} onClick={() => current.retryValue ? save(current.retryValue) : setRevision(value => value + 1)}>Retry confidence</button></div>}
    </section>;
}

export function PracticeActivityStatus({ activity }) {
    if (activity.failed) return <div className="iq-notice iq-activity-status" role="alert"><p>Your answers are saved. {activity.failed === 1 ? 'A practice activity update' : `${activity.failed} practice activity updates`} could not reach your study tracker.</p><button type="button" className="iq-secondary" disabled={Boolean(activity.pending)} onClick={activity.retry}>Retry tracker update</button></div>;
    if (activity.pending) return <p className="iq-activity-status" role="status">Updating your study tracker… You can continue practising.</p>;
    return null;
}
